import axiosInstance from '../../api';
import API_ENDPOINTS from '../../api/endpoints';
import useAuthStore from '../../../Store/authStore';
import useGeneralStore from '../../../Store/generalStore';
import { parseApiResponse, extractData, getErrorMessage, isSuccessResponse } from '../../api/responseModel';
import { notifyApiError } from '../../api/errorUtils';

const useAuth = () => {
    const { setTokens, logout: logoutStore } = useAuthStore();
    const { setLoading, setMessage, setError } = useGeneralStore();

    /**
     * Login user
     * @param {Object} credentials - { email, password }
     * @returns {Promise} Response data with tokens
     */
    const login = async (credentials) => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse);
                
                if (data && data.accessToken) {
                    // Store tokens and decode user info
                    setTokens(data.accessToken, data.refreshToken || null);
                    setMessage(apiResponse.responseMessage || 'Login successful!');
                    return data;
                } else {
                    throw new Error('No token received from server');
                }
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Login failed. Please try again.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Login failed. Please try again.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Register new user
     * @param {Object} userData - { firstName, lastName, email, phoneNumber, address, password, confirmPassword }
     * @returns {Promise} Response data
     */
    const register = async (userData) => {
        try {
            setLoading(true);
            // Remove confirmPassword and map phone to phoneNumber before sending to API
            const { confirmPassword, phone, ...restData } = userData;
            const registrationData = {
                ...restData,
                phoneNumber: phone || restData.phoneNumber, // Support both phone and phoneNumber
            };
            
            const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REGISTER, registrationData);
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'Registration successful! Please login.');
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Registration failed. Please try again.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Registration failed. Please try again.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Logout user - Revokes token on server
     * @returns {Promise} Response data
     */
    const logout = async () => {
        try {
            setLoading(true);
            
            // Call revoke API (no body required)
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REVOKE);
                const apiResponse = parseApiResponse(response);
                if (isSuccessResponse(apiResponse)) {
                    setMessage(apiResponse.responseMessage || 'Logged out successfully');
                }
            } catch (error) {
                // Even if API call fails, logout locally
                console.error('Revoke token API error:', error);
            }
            
            // Clear auth store
            logoutStore();
        } catch (error) {
            // Even if there's an error, logout locally
            logoutStore();
            console.error('Logout error:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Refresh access token (no body required)
     * @returns {Promise} New tokens
     */
    const refreshToken = async () => {
        try {
            // Call refresh endpoint with no body
            const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);

            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse);
                
                if (data && data.accessToken) {
                    setTokens(data.accessToken, data.refreshToken || null);
                    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
                } else {
                    throw new Error('No token received from refresh endpoint');
                }
            } else {
                // If refresh fails, logout user
                logoutStore();
                throw new Error(getErrorMessage(apiResponse) || 'Token refresh failed');
            }
        } catch (error) {
            // If refresh fails, logout user
            logoutStore();
            throw error;
        }
    };

    /**
     * Forgot password - sends OTP to email
     * @param {String} email - User email address
     * @returns {Promise} Response data
     */
    const forgotPassword = async (email) => {
        try {
            setLoading(true);
            // Use query parameter for email
            const response = await axiosInstance.post(
                `${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}?Email=${encodeURIComponent(email)}`
            );
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'OTP sent successfully!');
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to send OTP.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to send OTP.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Verify OTP
     * @param {Object} data - { email, otp }
     * @returns {Promise} Response data with challenge
     */
    const verifyOtp = async (data) => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
                email: data.email,
                otp: data.otp
            });
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'OTP verified successfully!');
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to verify OTP.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to verify OTP.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset password
     * @param {Object} data - { challenge, email, newPassword }
     * @returns {Promise} Response data
     */
    const resetPassword = async (data) => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
                challenge: data.challenge,
                email: data.email,
                newPassword: data.newPassword
            });
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'Password reset successfully!');
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to reset password.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to reset password.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Update user profile
     * @param {Object} data - { customerId, email, address, phoneNumber, firstName, lastName, image }
     * @returns {Promise} Response data
     */
    const updateProfile = async (data) => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(API_ENDPOINTS.USER.UPDATE_PROFILE, {
                customerId: data.customerId || 0,
                email: data.email || null,
                addressLine1: data.addressLine1 || null,
                addressLine2: data.addressLine2 || null,
                city: data.city || null,
                state: data.state || null,
                zipCode: data.zipCode || null,
                country: data.country || null,
                phoneNumber: data.phoneNumber || null,
                firstName: data.firstName || null,
                lastName: data.lastName || null,
                image: data.image || null
            });
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'Profile updated successfully!');
                
                // Update user info in store if token is refreshed or user data is returned
                // Note: You may need to refresh the token or update user store separately
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to update profile.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to update profile.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Change password
     * @param {Object} data - { customerId, oldPassword, newPassword }
     * @returns {Promise} Response data
     */
    const changePassword = async (data) => {
        try {
            setLoading(true);
            const response = await axiosInstance.post(API_ENDPOINTS.USER.CHANGE_PASSWORD, {
                customerId: data.customerId || 0,
                oldPassword: data.oldPassword,
                newPassword: data.newPassword
            });
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                setMessage(apiResponse.responseMessage || 'Password changed successfully!');
                return extractData(apiResponse);
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to change password.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to change password.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Upload image using base64
     * @param {Object} data - { base64, folder }
     * @returns {Promise} Response data with URL
     */
    const uploadImage = async (data) => {
        try {
            // Don't set global loading for image upload to avoid blocking UI
            const response = await axiosInstance.post(API_ENDPOINTS.UTILITIES.UPLOAD_BASE64_SINGLE, {
                base64: data.base64,
                folder: data.folder || 'Customers'
            });
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const responseData = extractData(apiResponse);
                return responseData?.url || null;
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to upload image.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to upload image.');
            throw error;
        }
    };

    return {
        login,
        register,
        logout,
        refreshToken,
        forgotPassword,
        verifyOtp,
        resetPassword,
        updateProfile,
        changePassword,
        uploadImage,
    };
};

export default useAuth;